// Calculator Engine for Freedom Number and Asset Allocation
// Based on practical experience and historical data

/**
 * Expense reduction factors by age bracket
 * Based on real-world observation: expenses reduce as you age
 */
const EXPENSE_REDUCTION_FACTORS = {
  '40-49': 1.0, // Full expenses
  '50-59': 0.95, // 5% reduction (kids independent, some costs reduce)
  '60-69': 0.85, // 15% reduction (housing paid off, work expenses gone)
  '70-79': 0.75, // 25% reduction (travel reduces, simpler lifestyle)
  '80+': 0.65, // 35% reduction (minimal needs, healthcare is major)
};

/**
 * Get expense reduction factor for given age
 */
export const getExpenseReductionFactor = (age) => {
  if (age < 50) return EXPENSE_REDUCTION_FACTORS['40-49'];
  if (age < 60) return EXPENSE_REDUCTION_FACTORS['50-59'];
  if (age < 70) return EXPENSE_REDUCTION_FACTORS['60-69'];
  if (age < 80) return EXPENSE_REDUCTION_FACTORS['70-79'];
  return EXPENSE_REDUCTION_FACTORS['80+'];
};

/**
 * UPDATED: Calculate Freedom Number - Quick Version
 * Uses 30-year inflation-adjusted withdrawal logic (4.5% inflation)
 */
export const calculateQuickFreedom = (monthlyExpenses, currentCorpus) => {
  const annualInflation = 0.045; // 4.5%
  const annualReturn = 0.085; // 8.5% nominal
  const years = 30;

  // Real rate of return calculation
  const realRate = (1 + annualReturn) / (1 + annualInflation) - 1;
  const monthlyRealRate = realRate / 12;
  const totalMonths = years * 12;

  // PV Formula: Amount needed to sustain monthly expense for 30 years
  const freedomNumber =
    monthlyExpenses * ((1 - Math.pow(1 + monthlyRealRate, -totalMonths)) / monthlyRealRate);

  const percentageComplete = Math.min((currentCorpus / freedomNumber) * 100, 100);
  const gap = Math.max(freedomNumber - currentCorpus, 0);

  return {
    freedomNumber,
    currentCorpus,
    percentageComplete,
    gap,
    status: percentageComplete >= 100 ? 'free' : percentageComplete >= 80 ? 'close' : 'planning',
  };
};

/**
 * Calculate comprehensive Freedom Number with detailed breakdown
 * (KEPT ORIGINAL FEATURE)
 */
export const calculateComprehensiveFreedom = (inputs) => {
  const {
    currentAge,
    retirementAge,
    lifeExpectancy,
    monthlyExpenses,
    currentCorpus,
    considerExpenseReduction,
    considerInflation,
    inflationRate,
  } = inputs;

  const retirementYears = [];

  for (let age = retirementAge; age < lifeExpectancy; age++) {
    const factor = considerExpenseReduction ? getExpenseReductionFactor(age) : 1.0;
    retirementYears.push({
      age,
      factor,
      adjustedMonthlyExpense: monthlyExpenses * factor,
    });
  }

  const avgFactor = retirementYears.reduce((sum, y) => sum + y.factor, 0) / retirementYears.length;
  const avgMonthlyExpense = monthlyExpenses * avgFactor;
  const avgAnnualExpense = avgMonthlyExpense * 12;

  const yearsToRetirement = retirementAge - currentAge;
  const inflationMultiplier = considerInflation
    ? Math.pow(1 + inflationRate / 100, yearsToRetirement)
    : 1.0;
  const inflationAdjustedAnnualExpense = avgAnnualExpense * inflationMultiplier;

  const freedomNumber = inflationAdjustedAnnualExpense / 0.05;

  const assetAllocation = {
    debt: {
      percentage: 40,
      amount: freedomNumber * 0.4,
      years: 7,
      purpose: 'Safe buffer for withdrawals',
    },
    equity: {
      percentage: 50,
      amount: freedomNumber * 0.5,
      purpose: 'Long-term growth engine',
    },
    gold: {
      percentage: 10,
      amount: freedomNumber * 0.1,
      purpose: 'Hedge against inflation & uncertainty',
    },
  };

  const percentageComplete = Math.min((currentCorpus / freedomNumber) * 100, 100);
  const gap = Math.max(freedomNumber - currentCorpus, 0);

  const monthsToRetirement = yearsToRetirement * 12;
  const requiredMonthlySavings = monthsToRetirement > 0 ? gap / monthsToRetirement : 0;

  const futureValue = (pmt, rate, nper) => {
    const r = rate / 12 / 100;
    return (pmt * (Math.pow(1 + r, nper) - 1)) / r;
  };

  const requiredSavingsWithReturn =
    monthsToRetirement > 0 ? gap / futureValue(1, 12, monthsToRetirement) : 0;

  return {
    freedomNumber,
    currentCorpus,
    gap,
    percentageComplete,
    status: percentageComplete >= 100 ? 'free' : percentageComplete >= 80 ? 'close' : 'planning',
    currentMonthlyExpense: monthlyExpenses,
    avgMonthlyExpenseInRetirement: avgMonthlyExpense,
    avgAnnualExpenseInRetirement: avgAnnualExpense,
    inflationAdjustedAnnualExpense,
    assetAllocation,
    yearsToRetirement,
    monthsToRetirement,
    requiredMonthlySavings,
    requiredSavingsWithReturn,
    expenseBreakdown: retirementYears,
    assumptions: {
      swr: 5,
      considerExpenseReduction,
      considerInflation,
      inflationRate,
      avgExpenseFactor: avgFactor,
    },
  };
};

/**
 * UPDATED: Calculate corpus sustainability
 * Capped at 45 years as requested
 */
export const calculateSustainability = (inputs) => {
  const { currentCorpus, monthlyWithdrawal, annualReturn, inflationRate } = inputs;

  let corpus = currentCorpus;
  let year = 0;
  const maxYears = 100; // Simulate up to 100 years
  const yearlyData = [];

  const monthlyReturn = annualReturn / 12 / 100;
  let currentWithdrawal = monthlyWithdrawal;

  while (corpus > 0 && year < maxYears) {
    year++;
    let yearStartCorpus = corpus;
    let yearWithdrawals = 0;

    for (let month = 1; month <= 12; month++) {
      corpus = corpus * (1 + monthlyReturn);
      corpus -= currentWithdrawal;
      yearWithdrawals += currentWithdrawal;
      if (corpus <= 0) {
        corpus = 0;
        break;
      }
    }

    yearlyData.push({
      year,
      startCorpus: yearStartCorpus,
      endCorpus: Math.max(corpus, 0),
      withdrawals: yearWithdrawals,
      withdrawal: currentWithdrawal,
    });

    currentWithdrawal = currentWithdrawal * (1 + inflationRate / 100);
    if (corpus <= 0) break;
  }

  // If corpus survived 100 years, it's essentially perpetual
  const isPerpetual = year >= maxYears && corpus > 0;

  return {
    years: year,
    depleted: corpus <= 0,
    isPerpetual,
    finalCorpus: corpus,
    yearlyData,
    status: isPerpetual
      ? 'excellent'
      : year >= 30
        ? 'excellent'
        : year >= 20
          ? 'good'
          : year >= 15
            ? 'caution'
            : 'risky',
  };
};

/**
 * Calculate Plan B (Relocation) scenarios
 * (KEPT ORIGINAL FEATURE)
 */
export const calculatePlanB = (inputs) => {
  const {
    currentCorpus,
    currentCity,
    homeValue,
    targetCity,
    newHomeCost,
    currentMonthlyExpense,
    costMultiplier,
  } = inputs;

  const netFromHome = homeValue - newHomeCost;
  const newCorpus = currentCorpus + netFromHome;
  const newMonthlyExpense = currentMonthlyExpense * costMultiplier;
  const currentAnnualExpense = currentMonthlyExpense * 12;
  const newAnnualExpense = newMonthlyExpense * 12;
  const currentFreedomNumber = currentAnnualExpense / 0.05;
  const newFreedomNumber = newAnnualExpense / 0.05;

  const calculateRunway = (corpus, annualExpense) => {
    const swr = 0.05;
    return corpus / (annualExpense * swr);
  };

  const currentRunway = calculateRunway(currentCorpus, currentAnnualExpense);
  const newRunway = calculateRunway(newCorpus, newAnnualExpense);
  const extensionYears = newRunway - currentRunway;

  return {
    current: {
      city: currentCity,
      corpus: currentCorpus,
      homeValue,
      monthlyExpense: currentMonthlyExpense,
      freedomNumber: currentFreedomNumber,
      runway: currentRunway,
    },
    relocated: {
      city: targetCity,
      corpus: newCorpus,
      homeCost: newHomeCost,
      netFromHome,
      monthlyExpense: newMonthlyExpense,
      freedomNumber: newFreedomNumber,
      runway: newRunway,
    },
    improvement: {
      extensionYears,
      monthlySavings: currentMonthlyExpense - newMonthlyExpense,
      annualSavings: (currentMonthlyExpense - newMonthlyExpense) * 12,
      corpusIncrease: netFromHome,
      costReduction: ((currentMonthlyExpense - newMonthlyExpense) / currentMonthlyExpense) * 100,
    },
  };
};

export const formatCurrency = (amount) => {
  // Round to nearest thousand for cleaner display
  amount = Math.round(amount / 1000) * 1000;

  if (amount >= 10000000) {
    const crores = amount / 10000000;
    // Show decimal only if there's a significant remainder, remove trailing zeros
    if (crores % 1 === 0) {
      return `₹ ${crores.toFixed(0)} Cr`;
    }
    return `₹ ${parseFloat(crores.toFixed(2))} Cr`;
  }
  if (amount >= 100000) {
    const lakhs = amount / 100000;
    if (lakhs % 1 === 0) {
      return `₹ ${lakhs.toFixed(0)} Lakhs`;
    }
    return `₹ ${parseFloat(lakhs.toFixed(2))} Lakhs`;
  }
  return `₹ ${amount.toLocaleString('en-IN')}`;
};

// Convert number to Indian words (Lakhs, Crores system)
export const amountToWords = (num) => {
  if (num === 0) return 'Zero';
  if (!num || isNaN(num)) return '';

  num = Math.round(num);

  const ones = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];
  const tens = [
    '',
    '',
    'Twenty',
    'Thirty',
    'Forty',
    'Fifty',
    'Sixty',
    'Seventy',
    'Eighty',
    'Ninety',
  ];

  const numToWords = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000)
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + numToWords(n % 100) : '');
    return '';
  };

  // Indian number system: Crores, Lakhs, Thousands
  if (num >= 10000000) {
    const crores = Math.floor(num / 10000000);
    const remainder = num % 10000000;
    let result = numToWords(crores) + ' Crore';
    if (remainder >= 100000) {
      result += ' ' + amountToWords(remainder);
    } else if (remainder > 0) {
      result += ' ' + amountToWords(remainder);
    }
    return result;
  }

  if (num >= 100000) {
    const lakhs = Math.floor(num / 100000);
    const remainder = num % 100000;
    let result = numToWords(lakhs) + ' Lakh';
    if (remainder >= 1000) {
      result += ' ' + amountToWords(remainder);
    } else if (remainder > 0) {
      result += ' ' + numToWords(remainder);
    }
    return result;
  }

  if (num >= 1000) {
    const thousands = Math.floor(num / 1000);
    const remainder = num % 1000;
    let result = numToWords(thousands) + ' Thousand';
    if (remainder > 0) {
      result += ' ' + numToWords(remainder);
    }
    return result;
  }

  return numToWords(num);
};

export const formatPercentage = (value, decimals = 1) => `${value.toFixed(decimals)}%`;

export const calculateCAGR = (startValue, endValue, years) =>
  (Math.pow(endValue / startValue, 1 / years) - 1) * 100;

export const getRecommendedAllocation = (age) => {
  let equityPercent;
  if (age < 40) equityPercent = 70;
  else if (age < 50) equityPercent = 60;
  else if (age < 60) equityPercent = 50;
  else if (age < 70) equityPercent = 40;
  else equityPercent = 30;

  const goldPercent = 10;
  const debtPercent = 100 - equityPercent - goldPercent;
  return { equity: equityPercent, debt: debtPercent, gold: goldPercent };
};
